/**
 * @file src/_modulos/prenota/tabela/layouts/filters/AdvancedFilters.tsx
 * @description Componente simplificado de filtros avançados
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza filtros avançados
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useAdvancedFilters, FILTER_COLUMNS, SELECT_OPTIONS, useExportToExcel } from "@PN/tabela";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuItem,
  Badge,
  Input,
  Label,
  DatePicker,
  Tooltiper,
  fmt,
} from "visuais";
import { toast } from "react-hot-toast";
import {
  IconFilterOff,
  IconMistOff,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { FileSpreadsheetIcon, InfinityIcon } from "lucide-react";

interface AdvancedFiltersProps {
  data?: any[];
}

/**
 * Componente de filtros avançados
 */
export const AdvancedFilters = ({ data = [] }: AdvancedFiltersProps) => {
  const { dropdownState, activeFilters, sorting, handlers, helpers, condicoesOptions } =
    useAdvancedFilters();

  const { exportToExcel } = useExportToExcel();

  const { isOpen: isDropdownOpen, currentFilter } = dropdownState;

  const {
    handleApplyFilter,
    handleRemoveFilter,
    handleClearClassification,
    handleClearAllFilters,
    setIsDropdownOpen,
    setCurrentFilter,
  } = handlers;

  const { getColumnType, getCurrentValue, formatDate, parseDate } = helpers;

  // Função para exportar dados
  const handleExportToExcel = async () => {
    try {
      await exportToExcel({
        data: [], // Não usado mais - busca tudo da API
        filters: activeFilters.reduce((acc, filter) => {
          acc[filter.id] = filter.value;
          return acc;
        }, {} as Record<string, any>),
        sorting,
      }, {
        filename: `prenotas_${new Date().toISOString().split("T")[0]}.xlsx`,
        sheetName: "Pré-Notas",
      });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast.error("Erro ao exportar dados");
    }
  };

  // Sanitização é feita no handler central em useAdvancedFilters
  return (
    <div className="flex items-center gap-2 relative min-w-0">
      {/* Filtros ativos */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        {activeFilters.map((filter) => (
          <Badge
            key={filter.id}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-2 flex-shrink-0"
          >
            <span className="text-xs truncate flex items-center gap-1">
              {filter.id === "F1_VALBRUT" ? (
                <>
                  {filter.label}
                  {filter.value.min != null
                    ? fmt.numbers.currency(filter.value.min)
                    : fmt.numbers.currency(0)}
                  {" até "}
                  {filter.value.max != null ? (
                    fmt.numbers.currency(filter.value.max)
                  ) : (
                    <InfinityIcon className="size-4 inline" />
                  )}
                </>
              ) : (
                filter.label
              )}
            </span>
            <button
              onClick={() => handleRemoveFilter(filter.id)}
              className="ml-1 hover:bg-muted rounded-full p-0.5 flex-shrink-0"
            >
              <IconX className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Botão de adicionar filtro */}
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <Tooltiper variant="info" sideOffset={10} content="Adicionar filtro">
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              className="border border-input flex-shrink-0"
            >
              <IconPlus className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </Tooltiper>
        <DropdownMenuContent align="end" className="w-48">
          {FILTER_COLUMNS.map((column) => (
            <DropdownMenuSub key={column.key}>
              <DropdownMenuSubTrigger>{column.label}</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="w-80 p-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor={`value-${column.key}`}>
                        {getColumnType(column.key) === "select"
                          ? "Selecionar valor"
                          : "Valor"}
                      </Label>

                      {getColumnType(column.key) === "select" ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between"
                            >
                              {getCurrentValue(column.key) || "Selecione..."}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-full">
                            {column.key === "F1_COND" ? (
                              // Opções dinâmicas para condições de pagamento
                              condicoesOptions.map((option: any) => (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() =>
                                    setCurrentFilter((prev: any) => ({
                                      ...prev,
                                      column: column.key,
                                      value: option.value,
                                    }))
                                  }
                                >
                                  {option.label}
                                </DropdownMenuItem>
                              ))
                            ) : (
                              // Opções estáticas para outros campos
                              SELECT_OPTIONS[
                                column.key as keyof typeof SELECT_OPTIONS
                              ]?.map((option: any) => (
                                <DropdownMenuItem
                                  key={option}
                                  onClick={() =>
                                    setCurrentFilter((prev: any) => ({
                                      ...prev,
                                      column: column.key,
                                      value: option,
                                    }))
                                  }
                                >
                                  {option}
                                </DropdownMenuItem>
                              ))
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : getColumnType(column.key) === "date" ? (
                        <DatePicker
                          mode={(column as any).useRange ? "range" : "single"}
                          value={
                            (column as any).useRange
                              ? getCurrentValue(column.key)
                                ? {
                                    from: parseDate(
                                      getCurrentValue(column.key).split(
                                        " - "
                                      )[0]
                                    ),
                                    to: parseDate(
                                      getCurrentValue(column.key).split(
                                        " - "
                                      )[1]
                                    ),
                                  }
                                : undefined
                              : parseDate(getCurrentValue(column.key))
                          }
                          onChange={(date) => {
                            if (date) {
                              let value: string;
                              if (
                                (column as any).useRange &&
                                typeof date === "object" &&
                                "from" in date
                              ) {
                                const range = date as { from?: Date; to?: Date };
                                value =
                                  range.from && range.to
                                    ? `${formatDate(range.from)} - ${formatDate(
                                        range.to
                                      )}`
                                    : range.from
                                    ? formatDate(range.from)
                                    : "";
                              } else {
                                value = formatDate(date as Date);
                              }

                              setCurrentFilter((prev: any) => ({
                                ...prev,
                                column: column.key,
                                value,
                              }));
                            }
                          }}
                          allowTyping={!(column as any).useRange}
                        />
                      ) : getColumnType(column.key) === "number" &&
                        (column as any).useRange ? (
                        <div className="space-y-2">
                          <Label>Mínimo</Label>
                          <Input
                            type="number"
                            value={currentFilter.minValue || ""}
                            onChange={(e) =>
                              setCurrentFilter((prev: any) => ({
                                ...prev,
                                column: column.key,
                                minValue: e.target.value,
                                value: { min: e.target.value, max: prev.maxValue }, 
                              }))
                            }
                            placeholder="Valor mínimo"
                          />

                          <Label>Máximo</Label>
                          <Input
                            type="number"
                            value={currentFilter.maxValue || ""}
                            onChange={(e) =>
                              setCurrentFilter((prev: any) => ({
                                ...prev,
                                column: column.key,
                                maxValue: e.target.value,
                                value: { min: prev.minValue, max: e.target.value }, 
                              }))
                            }
                            placeholder="Valor máximo"
                          />
                        </div>
                      ) : (
                        <Input
                          id={`value-${column.key}`}
                          type={getColumnType(column.key)}
                          value={getCurrentValue(column.key)}
                          onChange={(e) => {
                            setCurrentFilter((prev: any) => ({
                              ...prev,
                              column: column.key,
                              value: e.target.value,
                            }));
                          }}
                          placeholder={`Digite ${column.label.toLowerCase()}`}
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        handleApplyFilter();
                      }}
                      disabled={
                        !currentFilter.value &&
                        !currentFilter.minValue &&
                        !currentFilter.maxValue
                      }
                      className="flex-1"
                    >
                      Aplicar
                    </Button>
                  </div>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Botões de limpar */}
      <div className="flex gap-2 items-center">
        <Tooltiper content="Limpar todos os filtros">
          <Button
            variant="destructive"
            onClick={handleClearAllFilters}
            disabled={activeFilters.length === 0}
          >
            <IconFilterOff className="h-4 w-4" />
          </Button>
        </Tooltiper>
        <Tooltiper content="Limpar classificação">
          <Button
            variant="destructive"
            onClick={handleClearClassification}
            disabled={sorting.length === 0}
          >
            <IconMistOff className="h-4 w-4" />
          </Button>
        </Tooltiper>
        <Tooltiper content="Exportar para Excel">
          <Button
            onClick={handleExportToExcel}
            className="bg-green-500/40 hover:bg-green-500/60 text-white border border-green-500/60"
          >
            <FileSpreadsheetIcon className="h-4 w-4" />
          </Button>
        </Tooltiper>
      </div>
    </div>
  );
};
